/** SUNTRONIX ONE — Spec Conflict Queue.
 *  Technology → Spec Conflicts. Surfaces products whose detected data disagrees
 *  with the master (unsupported description spec, colour/image mismatch, or an
 *  internal PRD id used as the customer part number) BEFORE they reach a quote.
 */
import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ShieldAlert, Loader2, RefreshCw, Check, ExternalLink } from "lucide-react";

const TYPE = {
  PART_NUMBER: { label: "Part number", tone: "bg-fuchsia-100 text-fuchsia-700 border-fuchsia-200", to: "/technology/part-number-enrichment" },
  COLOUR_IMAGE: { label: "Colour / image", tone: "bg-rose-100 text-rose-700 border-rose-200", to: "/technology/colour-enrichment" },
  DESCRIPTION_SPEC: { label: "Description spec", tone: "bg-amber-100 text-amber-800 border-amber-200", to: "/technology/description-enrichment" },
  REVIEW: { label: "Review", tone: "bg-slate-100 text-slate-600 border-slate-200", to: "/technology/coverage-centre" },
};

export default function SpecConflicts() {
  const nav = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/coverage/conflicts", { params: { page_size: 100 } })
      .then(({ data }) => setData(data))
      .catch(() => toast.error("Failed to load conflicts"))
      .finally(() => setLoading(false));
  }, []);
  useEffect(() => { load(); }, [load]);

  const clear = async (p) => {
    setBusy(p.id);
    try {
      await api.post(`/coverage/conflicts/${p.id}/clear`);
      toast.success("Marked as reviewed");
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
    finally { setBusy(null); }
  };

  return (
    <div className="p-4 md:p-6 max-w-5xl mx-auto space-y-5" data-testid="spec-conflicts-page">
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="h-11 w-11 rounded-xl bg-rose-600 grid place-items-center text-white"><ShieldAlert className="h-5 w-5" /></div>
          <div>
            <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: "Manrope, sans-serif" }}>Spec Conflict Queue</h1>
            <p className="text-[13px] text-slate-500">Products whose detected data disagrees with the master — resolve before they reach a customer quote.</p>
          </div>
        </div>
        <Button variant="outline" onClick={load} data-testid="conflicts-refresh"><RefreshCw className="h-4 w-4" /></Button>
      </div>

      {data && (
        <div className="flex flex-wrap gap-2" data-testid="conflicts-summary">
          <Badge className="text-[11px] border bg-slate-900 text-white">{data.total} open</Badge>
          {Object.entries(data.by_type || {}).map(([k, v]) => (
            <Badge key={k} className={"text-[11px] border " + (TYPE[k]?.tone || TYPE.REVIEW.tone)}>{TYPE[k]?.label || k}: {v}</Badge>
          ))}
        </div>
      )}

      <div className="rounded-xl border border-slate-200 bg-white overflow-x-auto">
        <table className="w-full text-[12.5px]" data-testid="conflicts-table">
          <thead><tr className="bg-slate-50 text-left text-[10px] uppercase tracking-wide text-slate-500">
            {["Product", "Brand", "Conflict", "Detail", ""].map((h, i) => <th key={i} className="px-3 py-2 font-semibold">{h}</th>)}
          </tr></thead>
          <tbody className="divide-y divide-slate-100">
            {loading && <tr><td colSpan={5} className="px-3 py-8 text-center text-slate-400"><Loader2 className="h-5 w-5 animate-spin inline" /></td></tr>}
            {!loading && (data?.items || []).length === 0 && <tr><td colSpan={5} className="px-3 py-10 text-center text-slate-400">No spec conflicts — catalogue is clean 🎉</td></tr>}
            {!loading && (data?.items || []).map((p) => {
              const c = p.conflict || {}; const t = TYPE[c.type] || TYPE.REVIEW;
              return (
                <tr key={p.id} className="hover:bg-slate-50" data-testid={`conflict-row-${p.id}`}>
                  <td className="px-3 py-2 text-slate-800 max-w-[240px] truncate" title={p.product_name}>{p.product_name}</td>
                  <td className="px-3 py-2 text-slate-500">{p.brand || "—"}</td>
                  <td className="px-3 py-2"><Badge className={"text-[10px] border " + t.tone}>{t.label}</Badge></td>
                  <td className="px-3 py-2 text-slate-500 max-w-[280px] truncate" title={c.detail}>{c.detail}</td>
                  <td className="px-3 py-2 text-right whitespace-nowrap">
                    {busy === p.id ? <Loader2 className="h-4 w-4 animate-spin inline text-slate-400" /> : (
                      <div className="flex items-center gap-1 justify-end">
                        <button onClick={() => nav(t.to)} title="Open workbench to fix" data-testid={`conflict-fix-${p.id}`}
                          className="p-1.5 rounded hover:bg-slate-100 text-slate-500"><ExternalLink className="h-3.5 w-3.5" /></button>
                        <button onClick={() => clear(p)} title="Mark reviewed" data-testid={`conflict-clear-${p.id}`}
                          className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600"><Check className="h-3.5 w-3.5" /></button>
                      </div>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
